<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  Cpu,
  Wifi,
  BarChart3,
  ShieldCheck,
  Bell,
  Settings2,
  Cloud,
  Smartphone,
} from "lucide-vue-next";

interface FeaturesProps {
  icon: string;
  title: string;
  description: string;
}

const featureList: FeaturesProps[] = [
  {
    icon: "cpu",
    title: "边缘计算处理",
    description:
      "本地化数据处理能力，减少网络延迟，提升响应速度，确保关键业务的实时性和稳定性。",
  },
  {
    icon: "wifi",
    title: "无线物联接入",
    description:
      "支持多种无线通信协议，包括WiFi、蓝牙、LoRa等，实现设备快速便捷接入物联网络。",
  },
  {
    icon: "barChart3",
    title: "智能数据分析",
    description:
      "AI驱动的数据挖掘和分析引擎，自动识别数据模式，生成业务洞察和优化建议。",
  },
  {
    icon: "shieldCheck",
    title: "多重安全保障",
    description:
      "端到端加密传输，访问权限管理，异常行为检测，全方位保护企业数据资产安全。",
  },
  {
    icon: "bell",
    title: "智能预警系统",
    description:
      "基于机器学习的异常检测，提前发现设备故障征兆，支持多渠道告警推送。",
  },
  {
    icon: "cloud",
    title: "云端协同管理",
    description:
      "混合云架构支持，本地部署与云端服务无缝结合，数据同步备份，随时随地访问。",
  },
];

const iconMap: Record<
  string,
  | typeof Cpu
  | typeof Wifi
  | typeof BarChart3
  | typeof ShieldCheck
  | typeof Bell
  | typeof Settings2
  | typeof Cloud
  | typeof Smartphone
> = {
  cpu: Cpu,
  wifi: Wifi,
  barChart3: BarChart3,
  shieldCheck: ShieldCheck,
  bell: Bell,
  settings2: Settings2,
  cloud: Cloud,
  smartphone: Smartphone,
};
</script>

<template>
  <section id="features" class="features-section">
    <h2 class="features-subtitle">智能化产品特色</h2>
    <h2 class="features-title">工业4.0领先技术 · 智能仪表卓越性能</h2>
    <h3 class="features-description">
      集成前沿AI算法与物联网技术，打造高精度智能仪器仪表、工业自动化控制系统，
      为企业数字化转型和智能制造提供强有力的技术支撑。
    </h3>

    <div class="features-grid">
      <div v-for="{ icon, title, description } in featureList" :key="title" class="feature-item">
        <Card class="feature-card">
          <CardHeader class="feature-header">
            <div class="feature-icon-wrapper">
              <component :is="iconMap[icon]" class="feature-icon" />
            </div>
            <CardTitle>{{ title }}</CardTitle>
          </CardHeader>
          <CardContent class="feature-content">{{ description }}</CardContent>
        </Card>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Features 主要样式 */
.features-section {
  @apply container py-24 sm:py-32;
}

.features-subtitle {
  @apply text-lg text-primary text-center mb-2 tracking-wider;
}

.features-title {
  @apply text-3xl md:text-4xl text-center font-bold mb-4;
}

.features-description {
  @apply md:w-1/2 mx-auto text-xl text-center text-muted-foreground mb-8;
}

.features-grid {
  @apply grid sm:grid-cols-2 lg:grid-cols-3 gap-4;
}

.feature-item {
  @apply w-full;
}

.feature-card {
  @apply h-full bg-background border-0 shadow-none;
}

.feature-header {
  @apply flex justify-center items-center;
}

.feature-icon-wrapper {
  @apply bg-primary/20 p-2 rounded-full ring-8 ring-primary/10 mb-4;
}

.feature-icon {
  @apply size-6 text-primary;
}

.feature-content {
  @apply text-muted-foreground text-center;
}
</style>
